<script lang="ts">
  import {environment} from '$lib/env';
  import {user, userReducer, userToken} from '$lib/stores';
  import {goto} from '$app/navigation';
  import {serialize} from 'cookie';
  import {closeDialog} from "$lib/stores/dialogStores";
  import Button from "$lib/elements/Button.svelte";
  import PasswordIcon from "$lib/icons/PasswordIcon.svelte";
  import EmailIcon from "$lib/icons/EmailIcon.svelte";
  import EyeIcon from "$lib/icons/EyeIcon.svelte";
  import EyeOffIcon from "$lib/icons/EyeOffIcon.svelte";
  import PromotionsIcon from "$lib/icons/PromotionsIcon.svelte";
  import CustomCheckbox from "$lib/elements/CustomCheckbox.svelte";
  import {getCookie, setCookie} from "$lib/utils/cookies";
  import {createForm} from "felte";
  import * as yup from "yup";
  import UserIcon from "$lib/icons/UserIcon.svelte";
  import {onMount} from "svelte";
  import {translate} from "$lib/i18n";

  let apiErrors: any[] = $state([]);
  let loading = $state(false);
  let passwordVisible = $state(false);
  let confirmPasswordVisible = $state(false);
  let passwordInput: HTMLInputElement = $state();
  let confirmPasswordInput: HTMLInputElement = $state();
  let isChecked: boolean = false;

  const schema = yup.object({
    email: yup.string().email().required(),
    username: yup.string().required().min(3),
    password: yup.string().required(),
    passwordConfirmation: yup.string()
            .required('Password confirmation is a required field')
            .oneOf([yup.ref('password'), ''], 'Passwords must match'),
    referralCode: yup.string().optional(),
    acceptTermsAndConditions: yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
    clickId: yup.string().optional()
  });

  const {form, errors, createSubmitHandler} = createForm({
    validate: async (values) => {
      try {
        await schema.validate(values, {abortEarly: false});
      } catch (err: any) {
        return err.inner.reduce((res: any, value: any) => ({
          [value.path]: value.message,
          ...res,
        }), {});
      }
    },
  });

  const altOnSubmit = createSubmitHandler({
    onSubmit: (values) => handleSubmit(),
  });

  let formData = $state({
    email: '',
    username: '',
    password: '',
    passwordConfirmation: '',
    referralCode: '',
    clickId: '',
    acceptTermsAndConditions: false
  });

  onMount(() => {
    if (localStorage.getItem('forza_referral')) {
      formData.referralCode = String(localStorage.getItem('forza_referral'));
    }
  });

  function handleCheckboxChange(event: CustomEvent<{ checked: boolean }>) {
    isChecked = event.detail.checked;
  }

  const handleSubmit = async (): Promise<void> => {
    loading = true;
    apiErrors = [];
    try {
      if (localStorage.getItem('forza_referral') && !formData.referralCode) {
        formData.referralCode = String(localStorage.getItem('forza_referral'));
      }
      if (localStorage.getItem('refcid') && !formData.referralCode) {
        formData.clickId = String(localStorage.getItem('refcid'));
      }

      const response = await fetch(`${environment.apiUrl}/registration`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const {errors} = await response.json();
        for (const key in errors) {
          if (errors.hasOwnProperty(key)) {
            apiErrors.push(errors[key]);
          }
        }
        throw new Error(errors || 'Registration failed');
      }

      const {data} = await response.json();

      document.cookie = serialize('forza_token', data.accessToken, {
        maxAge: 60 * 60 * 24 * 30 // 30 days
      });

      userToken.set('Bearer ' + data.accessToken);
      if (!getCookie('forza_token')) {
        setCookie('forza_token', data.accessToken, 30);
      }
      userReducer(data.user);
      await goto('/', {replaceState: true, noScroll: true}).then(() => {
        loading = false;
        closeDialog('RegistrationModal');
      });

    } catch (err: any) {
      loading = false;
    }
  };


  function handlePasswordVisibility(inputType: string) {
    if (inputType === 'password') {
      passwordVisible = !passwordVisible;
      passwordInput.type = passwordVisible ? 'text' : 'password';
    } else if (inputType === 'confirmPassword') {
      confirmPasswordVisible = !confirmPasswordVisible;
      confirmPasswordInput.type = confirmPasswordVisible ? 'text' : 'password';
    }
  }
</script>

<form class="w-full max-w-md px-4 max-h-[80vh] overflow-y-auto" use:form>
  <div class="text-center mb-4">
    <h2 class="text-2xl font-bold mb-1 text-white">{$translate('Create Account')}</h2>
    <p class="text-sm text-gray-400">Join Forza and start playing today</p>
  </div>

  {#if formData}
    <div class="flex flex-col gap-3">
      <!-- Email Field -->
      <div class="space-y-1">
        <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-2.5 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
          <EmailIcon/>
          <input
                  class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                  type="text"
                  id="email"
                  placeholder="Email"
                  name="email"
                  bind:value={formData.email}
          >
        </div>
        {#if $errors.email}
          <span class="text-red-400 text-xs pl-2 flex items-center -mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {$errors.email}
          </span>
        {/if}
      </div>

      <!-- Username Field -->
      <div class="space-y-1">
        <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-2.5 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
          <UserIcon/>
          <input
                  class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                  type="text"
                  id="username"
                  placeholder="Username"
                  name="username"
                  bind:value={formData.username}
          >
        </div>
        {#if $errors.username}
          <span class="text-red-400 text-xs pl-2 flex items-center -mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {$errors.username}
          </span>
        {/if}
      </div>

      <!-- Password Field -->
      <div class="space-y-1">
        <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-2.5 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
          <PasswordIcon/>
          <input
                  class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                  type="password"
                  bind:this={passwordInput}
                  id="password"
                  placeholder="Password"
                  name="password"
                  bind:value={formData.password}
          >
          <button
                  type="button"
                  class="focus:outline-none text-gray-400 hover:text-white transition-colors"
                  on:click={() => handlePasswordVisibility('password')}
          >
            {#if passwordVisible}
              <EyeIcon/>
            {:else}
              <EyeOffIcon/>
            {/if}
          </button>
        </div>
        {#if $errors.password}
          <span class="text-red-400 text-xs pl-2 flex items-center -mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {$errors.password}
          </span>
        {/if}
      </div>

      <!-- Confirm Password Field -->
      <div class="space-y-1">
        <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-2.5 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
          <PasswordIcon/>
          <input
                  class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                  type="password"
                  bind:this={confirmPasswordInput}
                  id="confirmPassword"
                  name="passwordConfirmation"
                  placeholder="Confirm Password"
                  bind:value={formData.passwordConfirmation}
          >
          <button
                  type="button"
                  class="focus:outline-none text-gray-400 hover:text-white transition-colors"
                  on:click={() => handlePasswordVisibility('confirmPassword')}
          >
            {#if confirmPasswordVisible}
              <EyeIcon/>
            {:else}
              <EyeOffIcon/>
            {/if}
          </button>
        </div>
        {#if $errors.passwordConfirmation}
          <span class="text-red-400 text-xs pl-2 flex items-center -mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            {$errors.passwordConfirmation}
          </span>
        {/if}
      </div>

      <!-- Promo Code Field -->
      <div class="space-y-1">
        <div class="rounded-xl bg-[#1A2234] border border-[#2A3547] w-full py-2.5 px-5 flex items-center transition-all duration-200 focus-within:border-[#FF3E00]/50 focus-within:shadow-[0_0_0_4px_rgba(255,62,0,0.1)]">
          <PromotionsIcon css="fill-white"/>
          <input
                  class="outline-0 w-full bg-transparent text-sm placeholder-gray-500 ml-3 text-white"
                  type="text"
                  id="promocode"
                  placeholder="Enter promo code (optional)"
                  bind:value={formData.referralCode}
          >
        </div>
      </div>
    </div>

    <!-- Terms and Conditions -->
    <div class="mt-3 mb-2">
      <div class="bg-[#1A2234]/50 border border-[#2A3547] rounded-lg p-3">
        <CustomCheckbox
                id="registerTermsAndConditions"
                name="acceptTermsAndConditions"
                bind:checked={formData.acceptTermsAndConditions}
                on:change={handleCheckboxChange}
        >
          <span class="ml-2 text-sm text-gray-300">
            {$translate('I agree to the')} <a href="https://forza.bet/terms-and-conditions" target="_blank"
                                              class="text-[#FF3E00] hover:text-[#FF5722] font-medium">{$translate('Terms and Conditions')}</a>
          </span>
        </CustomCheckbox>
      </div>

      {#if $errors.acceptTermsAndConditions}
        <span class="text-red-400 text-xs pl-2 mt-1 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {$errors.acceptTermsAndConditions}
        </span>
      {/if}
    </div>

    <!-- API Errors -->
    {#if apiErrors.length > 0}
      <div class="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
        {#each apiErrors as error}
          <div class="flex items-start mb-1 last:mb-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        {/each}
      </div>
    {/if}
  {/if}

  <!-- Submit Button -->
  <Button
          loading={loading}
          type="submit"
          on:click={altOnSubmit}
          css="w-full py-2.5 text-base font-medium mt-1"
  >
    {$translate('Create Account')}
  </Button>
</form>

<style>
  form {
    display: flex;
    flex-direction: column;
  }

  /* Subtle animation for focus states */
  input:focus {
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 62, 0, 0.1);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(255, 62, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 62, 0, 0);
    }
  }
</style>

